import { IAnnouncementBar } from '@/interfaces/homepage';
import { SetString } from '@/interfaces/location';
import { IProductDetails } from '@/interfaces/product';
import { IArea, IStoreLists } from '@/interfaces/store';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const SESSION_DURATION = 24 * 60 * 60 * 1000;
let expiryTimer: NodeJS.Timeout | null = null;

const useHeaderStore = create(
  persist(
    (set: any, get: any) => {
      // Centralised session-timer reset, gated on a real change.
      const resetSessionIfChanged = (hasChanged: boolean): void => {
        if (hasChanged) {
          get().updateSessionTimestamp();
        }
      };

      return {
      text: '',
      setText: (t: string) => set(() => ({ text: t })),

      navbarBackgroundColor: '',
      setNavbarBackgroundColor: (bg: string) => set({ navbarBackgroundColor: bg }),

      navbarTextColor: '',
      setNavbarTextColor: (navbarTextColor: string) => set({ navbarTextColor: navbarTextColor }),

      sessionTimestamp: Date.now(),

      clearSessionStorage: () => {
        sessionStorage.removeItem('header-storage');
        set(() => ({
          selectedStore: {} as IStoreLists,
          myLocation: {} as IArea,
          selectedStoreIds: [],
          selectedStoreCodes: [],
          expressMessage: '',
          isExpressEnabled: false,
          isStoreSelected: false,
          sessionTimestamp: Date.now(),
        }));
      },

      setupExpiryTimer: () => {
        if (expiryTimer) {
          clearTimeout(expiryTimer);
        }

        const state = get();
        const now = Date.now();
        const elapsed = now - state.sessionTimestamp;
        const remainingTime = Math.max(0, SESSION_DURATION - elapsed);

        expiryTimer = setTimeout(() => {
          console.log('Timer expired, clearing session storage');
          get().clearSessionStorage();
        }, remainingTime);
      },

      updateSessionTimestamp: () => {
        console.log('Updating timestamp and resetting timer');
        set(() => ({ sessionTimestamp: Date.now() }));
        get().setupExpiryTimer();
      },

      showPickInStoreOption: false,
      setShowPickInStoreOption: (showPickInStoreOption: boolean) => set(() => ({ showPickInStoreOption })),

      selectedStore: {} as IStoreLists,
      setSelectedStore: (selectedStore: IStoreLists) => {
        resetSessionIfChanged(Boolean(selectedStore?.Id));
        return set(() => ({
          selectedStore,
        }));
      },

      showExpressOption: false,
      setShowExpressOption: (showExpressOption: boolean) => set(() => ({ showExpressOption })),

      showCorporateDiscountOption: false,
      setShowCorporateDiscountOption: (showCorporateDiscountOption: boolean) => set(() => ({ showCorporateDiscountOption })),

      isExpressEnabled: false,
      setIsExpressEnabled: (isExpressEnabled: boolean) => {
        resetSessionIfChanged(get().isExpressEnabled !== isExpressEnabled);
        return set(() => ({ isExpressEnabled }));
      },

      isStoreSelected: false,
      setIsStoreSelected: (isStoreSelected: boolean) => set(() => ({ isStoreSelected })),

      expressMessage: '',
      setExpressMessage: (expressMessage: string) => {
        resetSessionIfChanged(get().expressMessage !== expressMessage);
        return set(() => ({ expressMessage }));
      },

      myLocation: {} as IArea,
      setMyLocation: (myLocation: IArea) => {
        resetSessionIfChanged(get().myLocation?.value !== myLocation?.value);
        return set(() => ({ myLocation }));
      },

      selectedStoreCodes: [] as string[],
      setSelectedStoreCodes: (selectedStoreCodes: string[]) => {
        const currentCodes = get().selectedStoreCodes ?? [];
        const nextCodes = selectedStoreCodes ?? [];
        const sameLength = currentCodes.length === nextCodes.length;
        const sameContents = sameLength && currentCodes.every((c: string, idx: number) => c === nextCodes[idx]);
        if (sameContents) {
          return;
        }
        return set(() => ({ selectedStoreCodes: nextCodes }));
      },

      selectedStoreIds: [] as string[],
      setSelectedStoreIds: (selectedStoreIds: string[]) => {
        const currentIds = get().selectedStoreIds ?? [];
        const nextIds = selectedStoreIds ?? [];
        const sameLength = currentIds.length === nextIds.length;
        const sameContents = sameLength && currentIds.every((id: string, idx: number) => id === nextIds[idx]);
        // Storm protection: a no-op `set` still allocates a new `selectedStoreIds`
        // array, which the writer effect in `useLocationModalQueries.ts` watches
        // and re-fires on, looping forever. Bail before the `set` call so the
        // effect's deps stay referentially stable.
        if (sameContents) {
          return;
        }
        resetSessionIfChanged(true);
        return set(() => ({ selectedStoreIds: nextIds }));
      },

      // Modal-session ids shared across all `<LocationModal>` instances
      // (Header + PreHeaderStoreSelector on home/PDP). Each previously held
      // independent `useState` which caused cross-instance state leakage —
      // moving them to the global store guarantees a single writer and
      // consistent reads across mounts.
      divisionId: '-1',
      setDivisionId: ((divisionId: string | ((prev: string) => string)) =>
        set((state: any) => ({
          divisionId:
            typeof divisionId === 'function'
              ? (divisionId as (prev: string) => string)(state.divisionId)
              : divisionId,
        }))) as SetString,

      districtId: '-1',
      setDistrictId: ((districtId: string | ((prev: string) => string)) =>
        set((state: any) => ({
          districtId:
            typeof districtId === 'function'
              ? (districtId as (prev: string) => string)(state.districtId)
              : districtId,
        }))) as SetString,

      thanaId: '-1',
      setThanaId: ((thanaId: string | ((prev: string) => string)) =>
        set((state: any) => ({
          thanaId:
            typeof thanaId === 'function'
              ? (thanaId as (prev: string) => string)(state.thanaId)
              : thanaId,
        }))) as SetString,

      overlay: false,
      activateOverlay: (activate: boolean) =>
        set((state: any) => ({
          overlay: activate,
          searchPanel: activate ? state.searchPanel : activate,
          filterPanel: activate ? state.filterPanel : activate,
          navPanel: activate ? state.navPanel : activate,
          cartPanel: activate ? state.cartPanel : activate,
          isBopisPanelOpen: activate ? state.isBopisPanelOpen : activate,
        })),

      isBopisPanelOpen: false,
      setIsBopisPanelOpen: (isBopisPanelOpen: boolean) =>
        set((state: any) => {
          return {
            isBopisPanelOpen,
            overlay: isBopisPanelOpen,
            navPanel: isBopisPanelOpen ? false : state.navPanel,
            filterPanel: isBopisPanelOpen ? false : state.filterPanel,
            cartPanel: isBopisPanelOpen ? false : state.cartPanel,
            searchPanel: isBopisPanelOpen ? false : state.searchPanel,
          };
        }),

      searchPanel: false,
      openSearchPanel: (searchPanel: boolean) =>
        set((state: any) => {
          return {
            searchPanel,
            overlay: searchPanel,
            navPanel: searchPanel ? false : state.navPanel,
            filterPanel: searchPanel ? false : state.filterPanel,
            cartPanel: searchPanel ? false : state.cartPanel,
            isBopisPanelOpen: searchPanel ? false : state.isBopisPanelOpen,
          };
        }),

      filterPanel: false,
      openFilterPanel: (filterPanel: boolean) =>
        set((state: any) => {
          return {
            filterPanel,
            overlay: filterPanel,
            navPanel: filterPanel ? false : state.navPanel,
            cartPanel: filterPanel ? false : state.cartPanel,
            searchPanel: filterPanel ? false : state.searchPanel,
            isBopisPanelOpen: filterPanel ? false : state.isBopisPanelOpen,
          };
        }),

      navPanel: false,
      openNavPanel: (navPanel: boolean) =>
        set((state: any) => {
          return {
            navPanel,
            overlay: navPanel || state.searchPanel,
            searchPanel: navPanel ? false : state.searchPanel,
            filterPanel: navPanel ? false : state.filterPanel,
            cartPanel: navPanel ? false : state.cartPanel,
            isBopisPanelOpen: navPanel ? false : state.isBopisPanelOpen,
          };
        }),

      filtersAvailable: false,
      setFiltersAvailability: (filtersAvailable: boolean) => set((state: any) => ({ filtersAvailable })),

      banner: false,
      setBanner: (banner: boolean) => set((state: any) => ({ banner })),

      cartPanel: false,
      openCartPanel: (cartPanel: boolean) =>
        set((state: any) => ({
          cartPanel,
          overlay: cartPanel,
          navPanel: cartPanel ? false : state.navPanel,
          filterPanel: cartPanel ? false : state.filterPanel,
          searchPanel: cartPanel ? false : state.searchPanel,
        })),

      filterData: '',
      setFilterData: (filter: string) => set(() => ({ filterData: filter })),

      filterAttribute: [],
      setFilterAttribute: (attribute: any) => set(() => ({ filterAttribute: attribute })),

      priceRange: { Min: 0, Max: 0 },
      setPriceRange: (price: any) => set(() => ({ priceRange: { Min: price.Min, Max: price.Max } })),
      styleImageMapping: {},
      setStyleImageMapping: (styleImage: any) => set(() => ({ styleImageMapping: styleImage })),
      styleSVGSourceCode: [],
      setStyleSVGSourceCode: (styleSvg: string[]) => set(() => ({ styleSVGSourceCode: styleSvg })),

      filterLoading: false,
      setFilterLoading: (loading: boolean) => set((state: any) => ({ filterLoading: loading })),

      routerState: '',
      setRouterState: (routerState: string) => set(() => ({ routerState: routerState })),

      showloaderOverlay: false,
      setShowloaderOverlay: (showloaderOverlay: boolean) => set(() => ({ showloaderOverlay: showloaderOverlay })),
      sortBy: '',
      setSortByState: (sort: string) => set(() => ({ sortBy: sort })),

      clearPageIndex: false,
      setClearPageIndex: (clearPageIndex: boolean) => set((state: any) => ({ clearPageIndex })),

      colorDictionary: [],
      setColorDictionary: (colors: any) => set(() => ({ colorDictionary: colors })),

      orderTrackerLoading: false,
      setOrderTrackerLoading: (orderTrackerLoading: boolean) => set((state: any) => ({ orderTrackerLoading })),
      quickView: false,
      openQuickView: (quickView: boolean) => set((state: any) => ({ quickView })),
      quickViewLoading: false,
      openQuickViewLoading: (quickViewLoading: boolean) => set((state: any) => ({ quickViewLoading })),

      quickViewProduct: {} as IProductDetails,
      setQuickViewProduct: (quickViewProduct: IProductDetails) => set((state: IProductDetails) => ({ quickViewProduct })),
      quickViewSeName: '',
      setQuickViewSeName: (quickViewSeName: string) => set((state: string) => ({ quickViewSeName })),

      isAnnouncementBarLoading: true,
      setIsAnnouncementBarLoading: (isAnnouncementBarLoading: boolean) => set(() => ({ isAnnouncementBarLoading })),
      announcementBarContent: null as IAnnouncementBar | null,
      setAnnouncementBarContent: (announcementBarContent: IAnnouncementBar | null) => set(() => ({ announcementBarContent })),
      };
    },
    {
      name: 'header-storage',
      storage: createJSONStorage(() => sessionStorage),

      partialize: (state: any) => ({
        selectedStore: state.selectedStore,
        myLocation: state.myLocation,
        selectedStoreIds: state.selectedStoreIds,
        selectedStoreCodes: state.selectedStoreCodes,
        expressMessage: state.expressMessage,
        isExpressEnabled: state.isExpressEnabled,
        isStoreSelected: state.isStoreSelected,
        sessionTimestamp: state.sessionTimestamp,
      }),

      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Error rehydrating storage', error);
          return;
        }
        if (state) {
          state.setupExpiryTimer();
        }
      },
    },
  ),
);

export default useHeaderStore;
